import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "reactstrap";
import ProductListColumnPicker from "../../pages/products/ProductListColumnPicker";
import ProductPickerFilters from "./ProductPickerFilters";
import HKPProductSummary, { DEFAULT_PRODUCT_FIELDS, OPTIONAL_PRODUCT_FIELDS } from "./HKPProductSummary";
import HKPProductViewDrawer from "./HKPProductViewDrawer";
import { initialProductFilters, PickerProduct, ProductPageLoader } from "./productSelection";
import { useProductPages } from "./useProductPages";
import InventoryPriceFilters from "./InventoryPriceFilters";
import "./productSelection.scss";

export type AddResult = { addedIds: string[]; rejectedIds?: string[] };
const EMPTY_IDS: string[] = [];
/** "Select all matching" loads at most this many products, 100 per request. */
export const SELECT_ALL_CAP = 500;
const SELECT_ALL_PAGE = 100;
type Props = {
  loadPage: ProductPageLoader;
  onAdd: (products: PickerProduct[]) => Promise<AddResult>;
  addedIds?: string[];
  initialExact?: string;
  busy?: boolean;
  progress?: string;
  feedback?: React.ReactNode;
  renderScanner?: (locate: (value: string) => void, disabled: boolean) => React.ReactNode;
  onDone: () => void;
  inventoryPricing?: boolean;
  priceFiltersEnabled?: boolean;
  renderPrices?: (product: PickerProduct, onSaved: (product: PickerProduct) => void) => React.ReactNode;
  addedReason?: string;
  doneLabel?: string;
  onSelectionChange?: (products: PickerProduct[]) => void;
  singleSelect?: boolean;
  /** Main button text for the current count; defaults to "Add Product(s)". */
  actionLabel?: (count: number) => string;
  busyLabel?: string;
};
export default function HKPProductInventoryPicker({ loadPage, onAdd, addedIds = EMPTY_IDS, initialExact = "", busy = false, progress, feedback, renderScanner, onDone,
  inventoryPricing = false, priceFiltersEnabled = false, renderPrices, addedReason = "Already on this memo", doneLabel = "Done", onSelectionChange, singleSelect = false, actionLabel, busyLabel = "Adding…" }: Props) {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setMobile(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const [filters, setFilters] = useState<Record<string, any>>(initialProductFilters);
  const [sort, setSort] = useState("updatedAt:-1");
  const [revision, setRevision] = useState(0);
  const [exact, setExact] = useState(initialExact);
  const [extraFields, setExtraFields] = useState<string[]>([]);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [selected, setSelected] = useState<Map<string, PickerProduct>>(new Map());
  const [view, setView] = useState<PickerProduct | null>(null);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [priceFeedback, setPriceFeedback] = useState("");
  const [selectingAll, setSelectingAll] = useState(false);
  const [bulkNote, setBulkNote] = useState("");
  const submitLock = useRef(false);
  const scroll = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const query = useMemo(() => ({ filters, sort, exact, revision }), [filters, sort, exact, revision]);
  const pages = useProductPages(query, loadPage);
  const locked = busy || submitting;
  const added = useMemo(() => new Set(addedIds), [addedIds]);
  const baseFields = DEFAULT_PRODUCT_FIELDS.map(([key]) => key);
  const fields = renderPrices ? ["name", "stock", "inventoryPrices", ...baseFields.filter((field) => field !== "name" && field !== "stock"), ...extraFields] : [...baseFields, ...extraFields];
  const count = selected.size;
  const reason = (product: PickerProduct) => added.has(product.productUniqueId) ? addedReason : product.canAdd === true ? "" : product.reason || "This product is not available for selection.";
  useEffect(() => { onSelectionChange?.(Array.from(selected.values())); }, [selected, onSelectionChange]);
  const priceSaved = (product: PickerProduct) => {
    pages.patchProduct(product);
    setView((current) => current?.productUniqueId === product.productUniqueId ? product : current);
    setSelected((previous) => {
      if (!previous.has(product.productUniqueId)) return previous;
      return new Map(previous).set(product.productUniqueId, product);
    });
    setPriceFeedback(`${product.name || product.productUniqueId}: prices saved${product.priceEditsRequirePublish ? " to draft. Publish the Product to update inventory filters" : ". Refreshing results; selected products stay selected"}.`);
    setRevision((value) => value + 1);
  };

  useEffect(() => { if (scroll.current) scroll.current.scrollTop = 0; }, [query]);
  useEffect(() => {
    if (!pages.more || pages.loading || pages.error || locked || !sentinel.current || !scroll.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void pages.next();
    }, { root: scroll.current, rootMargin: "160px" });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [pages.more, pages.loading, pages.error, pages.next, locked]);
  useEffect(() => {
    setSelected((previous) => {
      const next = new Map(previous);
      added.forEach((id) => next.delete(id));
      pages.products.forEach((product) => {
        if (product.canAdd !== true) next.delete(product.productUniqueId);
        else if (next.has(product.productUniqueId)) next.set(product.productUniqueId, product);
      });
      return next;
    });
  }, [added, pages.products]);

  const changeFilter = ({ name, value }: any) => {
    setExact("");
    setFilters((previous) => {
      const next: Record<string, any> = { ...previous, [name]: value };
      const hierarchy = ["artform", "type", "category", "subCategory", "weaveType"];
      const index = hierarchy.indexOf(name);
      if (index >= 0) hierarchy.slice(index + 1).forEach((field) => { next[field] = ""; });
      return next;
    });
  };
  const toggle = (product: PickerProduct) => {
    if (locked || reason(product)) return;
    setSelected((previous) => {
      const next = new Map(previous);
      if (next.has(product.productUniqueId)) next.delete(product.productUniqueId);
      else { if (singleSelect) next.clear(); next.set(product.productUniqueId, product); }
      return next;
    });
  };
  /** Select every addable product that matches the current filters (e.g. a price range), across pages. */
  const selectAllMatching = async () => {
    if (locked || singleSelect || selectingAll) return;
    setSelectingAll(true); setBulkNote("");
    try {
      const picked: PickerProduct[] = [];
      let skipped = 0, total = Number.POSITIVE_INFINITY, page = 0;
      while (page * SELECT_ALL_PAGE < total && picked.length < SELECT_ALL_CAP) {
        const result = await loadPage(query, page, SELECT_ALL_PAGE);
        total = result.total;
        if (!result.products.length) break;
        result.products.forEach((product) => {
          if (product.canAdd === true && !added.has(product.productUniqueId)) picked.push(product); else skipped += 1;
        });
        page += 1;
      }
      const chosen = picked.slice(0, SELECT_ALL_CAP);
      setSelected((previous) => {
        const next = new Map(previous);
        chosen.forEach((product) => next.set(product.productUniqueId, product));
        return next;
      });
      const capped = picked.length >= SELECT_ALL_CAP && total > SELECT_ALL_CAP;
      setBulkNote(`Selected ${chosen.length} matching product${chosen.length === 1 ? "" : "s"}`
        + (skipped ? ` · ${skipped} can't be added` : "")
        + (capped ? ` · first ${SELECT_ALL_CAP} only; narrow the filter for the rest` : "") + ".");
    } catch {
      setBulkNote("We couldn't select all matching products. Check your connection and try again.");
    } finally { setSelectingAll(false); }
  };
  const add = async () => {
    if (submitLock.current || locked || submitError || !selected.size) return;
    submitLock.current = true; setSubmitting(true); setSubmitError("");
    try {
      const result = await onAdd(Array.from(selected.values()));
      setRevision((value) => value + 1);
      setSelected((previous) => {
        const next = new Map(previous);
        [...result.addedIds, ...(result.rejectedIds || [])].forEach((id) => next.delete(id));
        return next;
      });
    } catch {
      setSubmitError("We couldn’t confirm the additions. Return to the document and check its contents before retrying.");
    } finally { submitLock.current = false; setSubmitting(false); }
  };
  const checkbox = (product: PickerProduct) => <input type="checkbox" aria-label={`Select ${product.name || product.productUniqueId}`}
    aria-describedby={reason(product) ? `reason-${product.productUniqueId}` : undefined}
    checked={selected.has(product.productUniqueId)} disabled={locked || Boolean(reason(product))} onChange={() => toggle(product)} />;
  return <section className="hkp-product-picker" aria-label="Product inventory picker">
    <fieldset disabled={locked} className="hkp-product-picker__controls">
      <ProductPickerFilters filters={filters} onChange={changeFilter} onReset={() => { setExact(""); setFilters(initialProductFilters()); setRevision((value) => value + 1); }}
        additionalFilters={priceFiltersEnabled ? <InventoryPriceFilters value={filters.price} onChange={(value) => changeFilter({ name: "price", value })} /> : undefined} />
      <div className="hkp-product-picker__toolbar">
        <label>Sort <select aria-label="Sort products" value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="updatedAt:-1">Recently Updated</option><option value="createdAt:-1">Recently Created</option><option value="createdAt:1">Oldest Created</option>
          {priceFiltersEnabled && <><option value="sellPriceAmount:1">Selling Price: Low to High</option><option value="sellPriceAmount:-1">Selling Price: High to Low</option></>}
        </select></label>
        <ProductListColumnPicker extraFields={extraFields} onChange={setExtraFields} open={columnsOpen} onOpenChange={setColumnsOpen}
          optionalColumns={OPTIONAL_PRODUCT_FIELDS.map(([field, label]) => ({ field, label, width: 2 }))} />
        {renderScanner?.((value) => { if (!locked) setExact(value); }, locked)}
      </div>
    </fieldset>
    {exact && <p role="status">Showing scanned product. <button type="button" disabled={locked} onClick={() => setExact("")}>Back to inventory</button></p>}
    {feedback}
    {priceFeedback && <p role="status">{priceFeedback}</p>}
    {!singleSelect && pages.total > 0 && <div className="hkp-product-picker__bulk">
      <button type="button" className="btn btn-hastkala-outline" disabled={locked || selectingAll} onClick={() => void selectAllMatching()}>
        {selectingAll ? "Selecting…" : `Select all ${Math.min(pages.total, SELECT_ALL_CAP).toLocaleString("en-IN")} matching`}
      </button>
      {count > 0 && <button type="button" className="btn btn-link" disabled={locked || selectingAll}
        onClick={() => { setSelected(new Map()); setBulkNote(""); }}>Clear selection</button>}
      {bulkNote && <span role="status">{bulkNote}</span>}
    </div>}
    {inventoryPricing && !priceFiltersEnabled && <p>Selling price filters are not available yet. You can still view prices and edit them if you have permission.</p>}
    {inventoryPricing && count > 0 && <p>Selected products stay selected when filters or prices change, including products outside the current results.</p>}
    {submitError && <p role="alert">{submitError}</p>}
    <div className="hkp-product-picker__scroll" ref={scroll} aria-label="Product results" tabIndex={0}>
      {!mobile ? <table className="geo-table hkp-product-picker__desktop"><thead><tr><th scope="col">Select</th><th scope="col">Image</th>
        {fields.map((field) => <th scope="col" key={field} className={field === "inventoryPrices" ? "hkp-product-picker__prices" : undefined}>{field === "inventoryPrices" ? "Selling Price / Cost Price" : [...DEFAULT_PRODUCT_FIELDS, ...OPTIONAL_PRODUCT_FIELDS].find(([key]) => key === field)?.[1]}</th>)}
      </tr></thead><tbody>{pages.products.map((product) => <tr key={product.productUniqueId} className={exact ? "is-located" : ""}>
        <td>{checkbox(product)}</td><HKPProductSummary product={product} table fields={fields} onView={() => setView(product)} reason={reason(product)}
          renderField={(field) => field === "inventoryPrices" ? renderPrices?.(product, priceSaved) : undefined} />
      </tr>)}</tbody></table> :
      <div className="hkp-product-picker__mobile">{pages.products.map((product) => <div className={`hkp-product-picker__card${exact ? " is-located" : ""}`} key={product.productUniqueId}>
        {checkbox(product)}<HKPProductSummary product={product} fields={["owner", "color", "type", "stock", "updatedAt"]} onView={() => setView(product)} reason={reason(product)} />
        {renderPrices && <div className="hkp-product-picker__card-prices">{renderPrices(product, priceSaved)}</div>}
      </div>)}</div>}
      {pages.loading && <p role="status">Loading products…</p>}
      {pages.error && <p role="alert">{pages.error} <button type="button" onClick={() => void pages.next()}>Retry loading products</button></p>}
      {!pages.loading && !pages.error && !pages.products.length && <p>No matching products. Try another name, HKP ID, tag or filter.</p>}
      <div ref={sentinel} data-testid="product-page-sentinel" />
      {!pages.loading && pages.products.length > 0 && <p className="hkp-product-picker__total">{pages.products.length} of {pages.total} products</p>}
    </div>
    <footer className="hkp-product-picker__footer">
      <span role="status">{progress || `${count} product${count === 1 ? "" : "s"} selected`}</span>
      <Button className="btn-hastkala-outline" disabled={locked} onClick={onDone}>{doneLabel}</Button>
      <Button color="primary" disabled={locked || Boolean(submitError) || count === 0} onClick={() => void add()}>{locked ? busyLabel : actionLabel ? actionLabel(count) : count > 1 ? `Add ${count} Products` : "Add Product"}</Button>
    </footer>
    <HKPProductViewDrawer product={view} onClose={() => setView(null)} />
  </section>;
}
